import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { ColyAvatar } from "@/components/ColyMascot";
import Wordmark from "@/components/Wordmark";

const LINKS = [
  { label: "Features", href: "/support?topic=feature" },
  { label: "Mood", href: "#mascot" },
  { label: "FAQ", href: "#faq" },
  { label: "Support", href: "/support" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // lock body scroll when mobile menu open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const handleAnchor = (e, href) => {
    if (href.startsWith("#")) {
      e.preventDefault();
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        setOpen(false);
      }
    }
  };

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-coly-cream/85 backdrop-blur-md border-b border-coly-ink/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1180px] mx-auto px-5 sm:px-6 h-[64px] sm:h-[72px] flex items-center justify-between">
        <a
          href="/"
          className="flex items-center gap-2.5 group"
          data-testid="navbar-logo"
        >
          <ColyAvatar size={34} />
          <Wordmark height={28} color="#3F7556" className="sm:!h-[32px]" />
        </a>

        {/* Desktop links — dark pill */}
        <nav className="hidden md:flex items-center bg-coly-ink rounded-2xl px-2 py-2 gap-1" data-testid="nav-pill">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => handleAnchor(e, l.href)}
              className="text-[14px] font-extrabold text-white/85 hover:text-white px-4 py-2 rounded-xl hover:bg-white/10 transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Desktop CTA */}
        <a
          href="#waitlist"
          onClick={(e) => handleAnchor(e, "#waitlist")}
          className="hidden md:inline-flex items-center gap-2 bg-coly-green text-white rounded-full px-4 py-2.5 font-extrabold text-[13px] hover:-translate-y-0.5 transition-transform"
          style={{ boxShadow: "0 3px 0 #4F8E6A" }}
          data-testid="nav-cta-join"
        >
          Join waitlist
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="2.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 12h14" />
            <path d="M13 5l7 7-7 7" />
          </svg>
        </a>

        {/* Mobile burger */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden w-10 h-10 rounded-full bg-white border border-coly-ink/10 flex items-center justify-center"
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="nav-burger"
        >
          {open ? (
            <X className="w-5 h-5 text-coly-ink" />
          ) : (
            <Menu className="w-5 h-5 text-coly-ink" />
          )}
        </button>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="drawer"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-coly-cream border-b border-coly-ink/10"
            data-testid="nav-mobile-drawer"
          >
            <nav className="px-5 py-5 flex flex-col gap-1">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => handleAnchor(e, l.href)}
                  className="px-3 py-3 rounded-2xl text-[16px] font-extrabold text-coly-ink hover:bg-white transition-colors"
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#waitlist"
                onClick={(e) => handleAnchor(e, "#waitlist")}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-coly-green text-white rounded-full px-5 py-3 font-extrabold text-[15px]"
                style={{ boxShadow: "0 3px 0 #4F8E6A" }}
                data-testid="nav-mobile-cta-join"
              >
                Join waitlist
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
